<template>
  <div class="min-h-screen">
    <!-- Hero Section - Full viewport split -->
    <section class="relative min-h-screen flex">
      <!-- Left Content -->
      <div
        class="relative z-10 w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 py-20 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700"
      >
        <div class="max-w-xl">
          <!-- Badge -->
          <div
            class="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full mb-6"
          >
            <span
              class="w-2 h-2 bg-accent-400 rounded-full animate-pulse"
            ></span>
            <span class="text-sm font-medium text-white/90"
              >Greenfield, Wisconsin</span
            >
          </div>

          <!-- Heading -->
          <h1
            class="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
          >
            Welcome to
            <span class="text-accent-300">Foxwood</span>
            Crossing
          </h1>

          <p class="text-lg text-white/80 mb-8 leading-relaxed">
            A close-knit community where neighbors become friends. Whether
            you're a current resident or considering making this your home,
            you're already part of the family.
          </p>

          <!-- CTAs -->
          <div class="flex flex-col sm:flex-row gap-4">
            <router-link
              to="/login"
              class="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-accent-500 text-white font-medium rounded-xl hover:bg-accent-600 transition-all duration-200 hover:shadow-lg hover:shadow-accent-500/30"
            >
              Resident Portal
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </router-link>
            <router-link
              to="/contact"
              class="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/15 text-white font-medium rounded-xl hover:bg-white/25 transition-all duration-200 backdrop-blur-sm border border-white/20"
            >
              Get in Touch
            </router-link>
          </div>
        </div>

        <!-- Scroll indicator -->
        <div
          class="absolute bottom-8 left-1/2 -translate-x-1/2 lg:left-16 lg:translate-x-0 hidden sm:flex flex-col items-center gap-2 text-white/60"
        >
          <span class="text-xs uppercase tracking-widest">Scroll</span>
          <div
            class="w-5 h-8 border-2 border-white/40 rounded-full flex justify-center"
          >
            <div
              class="w-1 h-2 bg-white/60 rounded-full mt-1.5 animate-bounce"
            ></div>
          </div>
        </div>
      </div>

      <!-- Right Image -->
      <div class="hidden lg:block w-1/2 relative">
        <img
          src="../assets/Entrance.png"
          alt="Foxwood Crossing neighborhood entrance"
          class="absolute inset-0 w-full h-full object-cover"
        />
        <div
          class="absolute inset-0 bg-gradient-to-r from-primary-700 via-primary-600/30 to-transparent"
        ></div>
      </div>

      <!-- Mobile background -->
      <div class="absolute inset-0 lg:hidden">
        <img
          src="../assets/Entrance.png"
          alt=""
          class="w-full h-full object-cover opacity-30"
        />
      </div>
    </section>

    <!-- Stats Bar -->
    <section class="bg-white border-b border-primary-100">
      <div
        class="max-w-screen-xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-8"
      >
        <div class="text-center">
          <div class="text-3xl font-heading font-bold text-primary-800">
            166
          </div>
          <div class="text-sm text-primary-500 mt-1">Homes</div>
        </div>
        <div class="text-center">
          <div class="text-3xl font-heading font-bold text-primary-800">
            1998
          </div>
          <div class="text-sm text-primary-500 mt-1">Established</div>
        </div>
        <div class="text-center">
          <div class="text-3xl font-heading font-bold text-primary-800">A+</div>
          <div class="text-sm text-primary-500 mt-1">School District</div>
        </div>
        <div class="text-center">
          <div class="text-3xl font-heading font-bold text-accent-600">
            <svg
              class="w-8 h-8 mx-auto"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              />
            </svg>
          </div>
          <div class="text-sm text-primary-500 mt-1">Community</div>
        </div>
      </div>
    </section>

    <!-- Bento Grid Section -->
    <section class="bg-primary-50 py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div class="text-center mb-12">
          <h2
            class="text-3xl sm:text-4xl font-heading font-bold text-primary-800 mb-4"
          >
            Life in Foxwood
          </h2>
          <p class="text-primary-500 max-w-2xl mx-auto">
            Everything you need is right around the corner. Great schools,
            beautiful parks, and a welcoming community.
          </p>
        </div>

        <!-- Bento Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Large Card - Schools -->
          <a
            href="https://www.whitnall.com/"
            target="_blank"
            class="group md:col-span-2 lg:col-span-1 lg:row-span-2 bg-gradient-to-br from-accent-600 to-accent-700 rounded-2xl p-8 text-white hover:shadow-xl hover:shadow-accent-600/20 transition-all duration-300 hover:-translate-y-1"
          >
            <div
              class="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
            >
              <svg
                class="w-7 h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 14l9-5-9-5-9 5 9 5z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                />
              </svg>
            </div>
            <h3 class="text-2xl font-heading font-bold mb-3">
              Whitnall School District
            </h3>
            <p class="text-white/80 leading-relaxed">
              Award-winning schools with dedicated teachers, modern facilities,
              and a focus on student success. One of the best districts in the
              Milwaukee metro area.
            </p>
            <div
              class="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/90 group-hover:text-white"
            >
              Learn more
              <svg
                class="w-4 h-4 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </div>
          </a>

          <!-- Parks -->
          <a
            href="https://www.ci.greenfield.wi.us/283/Parks-and-Recreation"
            target="_blank"
            class="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100"
          >
            <div
              class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
            >
              <svg
                class="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <h3
              class="text-lg font-heading font-semibold text-primary-800 mb-2"
            >
              Parks & Recreation
            </h3>
            <p class="text-sm text-primary-500">
              Beautiful green spaces, walking trails, and community programs for
              all ages.
            </p>
          </a>

          <!-- Library -->
          <a
            href="https://www.greenfieldlibrary.org/"
            target="_blank"
            class="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100"
          >
            <div
              class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
            >
              <svg
                class="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h3
              class="text-lg font-heading font-semibold text-primary-800 mb-2"
            >
              Public Library
            </h3>
            <p class="text-sm text-primary-500">
              Modern library with extensive resources, programs, and community
              events.
            </p>
          </a>

          <!-- Shopping -->
          <a
            href="https://www.google.com/search?q=Shopping+Greenfield+WI"
            target="_blank"
            class="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100"
          >
            <div
              class="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
            >
              <svg
                class="w-6 h-6 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
            </div>
            <h3
              class="text-lg font-heading font-semibold text-primary-800 mb-2"
            >
              Shopping & Dining
            </h3>
            <p class="text-sm text-primary-500">
              Convenient access to retail, restaurants, and entertainment.
            </p>
          </a>

          <!-- Entertainment -->
          <a
            href="https://www.tripadvisor.com/Attractions-g59934-Activities-Greenfield_Wisconsin.html"
            target="_blank"
            class="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100"
          >
            <div
              class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
            >
              <svg
                class="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3
              class="text-lg font-heading font-semibold text-primary-800 mb-2"
            >
              Things To Do
            </h3>
            <p class="text-sm text-primary-500">
              Explore attractions, events, and activities in the area.
            </p>
          </a>

          <!-- Neighborhood Dogs - Fun card -->
          <router-link
            to="/doggos"
            class="group bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl p-6 text-white hover:shadow-xl hover:shadow-rose-500/20 transition-all duration-300 hover:-translate-y-1"
          >
            <div
              class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-heading font-semibold mb-2">
              Meet the Doggos!
            </h3>
            <p class="text-sm text-white/80">
              The real stars of Foxwood Crossing.
            </p>
          </router-link>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="bg-white py-20">
      <div class="max-w-screen-xl mx-auto px-6">
        <div
          class="relative bg-gradient-to-br from-accent-600 to-accent-700 rounded-3xl overflow-hidden px-8 py-16 md:p-16"
        >
          <!-- Background pattern -->
          <div class="absolute inset-0 opacity-10">
            <svg
              class="w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <pattern
                  id="grid"
                  width="10"
                  height="10"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 10 0 L 0 0 0 10"
                    fill="none"
                    stroke="white"
                    stroke-width="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          <div class="relative z-10 max-w-2xl mx-auto text-center">
            <h2
              class="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
            >
              Thinking about joining the neighborhood?
            </h2>
            <p class="text-white/80 mb-8">
              We'd love to hear from you. Reach out to our HOA with any
              questions about life in Foxwood Crossing.
            </p>
            <router-link
              to="/contact"
              class="inline-flex items-center gap-2 px-8 py-4 bg-white text-accent-700 font-semibold rounded-xl hover:bg-accent-50 transition-all duration-200 shadow-lg shadow-black/10"
            >
              Contact the HOA
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-primary-900 text-white">
      <div class="max-w-screen-xl mx-auto px-6 py-12">
        <div
          class="flex flex-col md:flex-row justify-between items-center gap-6"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-accent-600 rounded-xl flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            </div>
            <div>
              <span class="font-heading font-semibold">Foxwood Crossing</span>
              <p class="text-xs text-primary-400">Greenfield, Wisconsin</p>
            </div>
          </div>

          <nav
            class="flex flex-wrap justify-center gap-6 text-sm text-primary-300"
          >
            <router-link to="/about" class="hover:text-white transition-colors"
              >About</router-link
            >
            <router-link to="/hoa" class="hover:text-white transition-colors"
              >HOA Info</router-link
            >
            <router-link
              to="/contact"
              class="hover:text-white transition-colors"
              >Contact</router-link
            >
            <router-link to="/events" class="hover:text-white transition-colors"
              >Events</router-link
            >
          </nav>
        </div>

        <div
          class="mt-8 pt-8 border-t border-primary-800 text-center text-sm text-primary-400"
        >
          &copy; {{ new Date().getFullYear() }} Foxwood Crossing Neighborhood
          Association
        </div>
      </div>
    </footer>
  </div>
</template>

<script lang="ts" setup></script>
